/**
 * KAN-837: вкладка "Профиль" приложения менеджера: свои ФИО, телефон (на
 * него звонит обратный звонок) и когда можно звонить. Логика формы в
 * useManagerProfileForm, ошибки backend показываются под полями.
 * KAN-835: ниже блок "Уведомления" (Web Push и свой Telegram).
 * KAN-871: блок "Мой Telegram" (личный аккаунт, писать клиентам от себя).
 * KAN-845: внизу пункт "Начало работы" (экран первых шагов открывается снова).
 * KAN-942: пункт "Полная версия" (кабинет в новой вкладке браузера, виден всем).
 */
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, ListChecks, Loader2, Monitor } from 'lucide-react';
import { MobileNotificationsBlock } from '@/components/mobile/MobileNotificationsBlock';
import { MobilePersonalTelegramBlock } from '@/components/mobile/MobilePersonalTelegramBlock';
import { ProfileField as Field, PHONE_HINT } from '@/components/mobile/ProfileField';
import { WorkHoursEditor } from '@/components/managers/WorkHoursEditor';
import { useManagerProfileForm } from '@/hooks/useManagerProfile';
import { CABINET_HOME_PATH, MOBILE_START_PATH } from '@/lib/mobileApp';
import { ONBOARDING_NAV_STATE } from '@/lib/managerOnboarding';
import { WORK_HOURS_LABEL } from '@/lib/workHours';

export function MobileProfilePage(): JSX.Element {
  const form = useManagerProfileForm();
  const { draft, errors, setField } = form;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void form.save();
  };

  return (
    <section className="px-4 py-4" data-testid="mobile-profile-page">
      <h1 className="mb-3 text-xl font-semibold text-gray-900">Профиль</h1>
      {!form.hasManager && (
        <p className="py-12 text-center text-sm text-gray-500">Профиль доступен менеджеру со своей записью</p>
      )}
      {form.hasManager && form.isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-label="Загрузка" />
        </div>
      )}
      {form.hasManager && form.isError && (
        <p className="py-8 text-center text-sm text-red-600">Не удалось загрузить профиль</p>
      )}
      {form.profile && (
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
            <Field
              name="last_name"
              label="Фамилия"
              value={draft.last_name}
              onChange={(v) => setField('last_name', v)}
              error={errors.last_name}
              autoComplete="family-name"
              maxLength={100}
            />
            <Field
              name="first_name"
              label="Имя"
              value={draft.first_name}
              onChange={(v) => setField('first_name', v)}
              error={errors.first_name}
              autoComplete="given-name"
              maxLength={100}
            />
            <Field
              name="middle_name"
              label="Отчество"
              value={draft.middle_name}
              onChange={(v) => setField('middle_name', v)}
              error={errors.middle_name}
              autoComplete="additional-name"
              maxLength={100}
            />
            <Field
              name="phone"
              label="Телефон"
              type="tel"
              inputMode="tel"
              value={draft.phone}
              onChange={(v) => setField('phone', v)}
              error={errors.phone}
              hint={PHONE_HINT}
              autoComplete="tel"
              placeholder="+7 900 000-00-00"
              maxLength={32}
            />
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <WorkHoursEditor
              label={WORK_HOURS_LABEL}
              value={draft.workHours}
              onChange={form.setWorkHours}
              timezone={form.profile.organization_timezone}
              error={errors.work_hours}
              disabled={form.saving}
            />
          </div>
          {form.formError && (
            <p role="alert" className="text-sm text-red-600" data-testid="profile-form-error">
              {form.formError}
            </p>
          )}
          {form.saved && (
            <p role="status" className="flex items-center gap-1.5 text-sm text-green-700" data-testid="profile-saved">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Сохранено
            </p>
          )}
          <button
            type="submit"
            disabled={form.saving}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-base font-medium text-white active:bg-blue-700 disabled:opacity-60"
          >
            {form.saving && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
            Сохранить
          </button>
        </form>
      )}
      <div className="mt-4">
        <MobileNotificationsBlock />
      </div>
      <div className="mt-4 empty:hidden">
        <MobilePersonalTelegramBlock />
      </div>
      {form.hasManager && (
        <Link
          to={MOBILE_START_PATH}
          state={ONBOARDING_NAV_STATE}
          className="mt-4 flex min-h-[56px] items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 text-base text-gray-900 active:bg-gray-100"
          data-testid="profile-onboarding-link"
        >
          <ListChecks className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
          <span className="flex-1">Начало работы</span>
          <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
        </Link>
      )}
      {/* KAN-942: обычная ссылка в новую вкладку: scope PWA /m/ не открывает кабинет внутри приложения (KAN-881) */}
      <a
        href={CABINET_HOME_PATH}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 flex min-h-[56px] items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 text-base text-gray-900 active:bg-gray-100"
        data-testid="profile-full-version-link"
      >
        <Monitor className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
        <span className="flex-1">Полная версия</span>
        <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
      </a>
    </section>
  );
}
